/**
 * KullaniciTalebiKararModallari.jsx — the two staff decisions on a user addition request (backlog #614; backend #612).
 *
 * KullaniciTalebiOnayModal: the request's fields, pre-filled and correctable, then ONE call (POST /kullanici-talepleri/{id}/onayla) creates
 * the login, links it to the company, gives the portal role, makes the ticked logins passive and closes the request; the activation mail goes
 * out after that and the window says whether it did (a mail that fails never undoes the login). The company is fixed (it asked for it), the
 * password is never typed (the mail's link sets it, #555 / #568). WAS planned as "opens UserFormModal pre-filled" (#614's first text): the
 * backend approves in one transaction instead of POST /users, so a form of its own sends the whole decision.
 *
 * E-mail already on another login (Deniz 2026-11-02, #612 e): the logins holding the address are listed, every one staff may pass over
 * (active, not staff / root) is PRE-TICKED to be made passive so only the new login uses it; staff un-tick the ones to keep. The list is
 * for the address as requested: when staff change the address the ticks are off and locked (the backend accepts only logins holding the
 * final address).
 *
 * KullaniciTalebiRedModal: the reason is required (the company reads it in its list), 500 characters at most.
 *
 * Both are mounted only while open by the page, so every opening reads the request afresh.
 */
import React, { useEffect, useState } from "react";
import {
  Alert, Badge, Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  getKullaniciTalebiDetay, onaylaKullaniciTalebi, reddetKullaniciTalebi, talepHatasi,
} from "../../../../services/kullaniciTalebiService";
import { getUserGroups } from "../../../../services/userService";
import { tarihSaat } from "../../../../Components/Kullanici/KullaniciParcalari";
import TelefonGirdisi from "../../../../Components/Common/TelefonGirdisi";

const RED_SEBEBI_EN_FAZLA = 500;

/** The mail part of an approval's answer as a sentence: sent / not sent (and why) / switched off. */
export function postaMetni(t, cevap, istendi) {
  if (!istendi) return t("yonetim:kullaniciTalepleri.postaKapaliydi");
  const mail = cevap?.aktivasyonMaili;
  if (mail?.sent) return t("yonetim:userFormModal.postaGitti", { adres: mail.to || cevap?.talep?.eposta || "" });
  return t("yonetim:userFormModal.postaGitmedi", { neden: mail?.error || mail?.message || mail?.status || "" });
}

// ─── Approve ─────────────────────────────────────────────────────────────────

export function KullaniciTalebiOnayModal({ talepId, onClose }) {
  const { t } = useTranslation("yonetim");
  const [detay, setDetay] = useState(null);
  const [grupAdlari, setGrupAdlari] = useState({});
  const [form, setForm] = useState(null);
  const [pasifSecim, setPasifSecim] = useState(new Set());
  const [postaGonder, setPostaGonder] = useState(true);
  const [okumaHatasi, setOkumaHatasi] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hata, setHata] = useState(null);
  const [bitis, setBitis] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const [d, gruplar] = await Promise.all([getKullaniciTalebiDetay(talepId), getUserGroups().catch(() => [])]);
        if (ignore) return;
        const r = d.talep;
        setDetay(d);
        setGrupAdlari(Object.fromEntries((gruplar || []).map((g) => [g.id, g.userGroupName])));
        setForm({
          kullaniciAdi: r.kullaniciAdi || "", ad: r.ad || "", soyad: r.soyad || "", eposta: r.eposta || "",
          telefon: r.telefon || "", unvan: r.unvan || "", portalRolId: String(r.portalRolId ?? ""),
          userGroupId: String(d.varsayilanUserGroupId ?? ""),
        });
        setPasifSecim(new Set((d.epostaKullananlar || []).filter((x) => x.pasifYapilabilir).map((x) => x.userId)));
      } catch (e) {
        if (!ignore) setOkumaHatasi(talepHatasi(e) || t("kullaniciTalepleri.okunamadi"));
      }
    }
    run();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talepId]);

  const kilitli = saving || bitis != null;
  const talep = detay?.talep;
  const epostaDegisti = form != null && talep != null && form.eposta.trim() !== (talep.eposta || "");
  const kullananlar = detay?.epostaKullananlar || [];

  const ayarla = (alan, deger) => setForm((f) => ({ ...f, [alan]: deger }));
  const kapat = () => {
    if (saving) return;
    onClose(bitis ? bitis.cevap : null);
  };

  const onayla = async () => {
    setHata(null);
    if (!form.kullaniciAdi.trim()) { setHata(t("kullaniciTalepleri.kullaniciAdiZorunlu")); return; }
    if (!form.ad.trim() || !form.soyad.trim()) { setHata(t("kullaniciTalepleri.adSoyadZorunlu")); return; }
    if (!form.eposta.trim()) { setHata(t("kullaniciTalepleri.epostaZorunlu")); return; }
    setSaving(true);
    try {
      const cevap = await onaylaKullaniciTalebi(talepId, {
        kullaniciAdi: form.kullaniciAdi.trim(),
        ad: form.ad.trim(),
        soyad: form.soyad.trim(),
        eposta: form.eposta.trim(),
        telefon: form.telefon.trim(),
        unvan: form.unvan.trim(),
        portalRolId: Number(form.portalRolId),
        userGroupId: Number(form.userGroupId),
        pasifYapilacakKullaniciIds: epostaDegisti ? [] : [...pasifSecim],
        aktivasyonMaili: postaGonder,
      });
      setBitis({ cevap, posta: postaMetni(t, { ...cevap, talep: { eposta: form.eposta.trim() } }, postaGonder), gitti: Boolean(cevap?.aktivasyonMaili?.sent) });
    } catch (e) {
      setHata(talepHatasi(e) || t("kullaniciTalepleri.onaylanamadi"));
    } finally {
      setSaving(false);
    }
  };

  const grupAdi = (id) => grupAdlari[id] || `#${id}`;

  return (
    <Modal isOpen toggle={kapat} size="lg">
      <ModalHeader toggle={kapat}>{t("kullaniciTalepleri.onayBaslik")}</ModalHeader>
      <ModalBody>
        {okumaHatasi && <Alert fade={false} color="danger" className="py-2 small">{okumaHatasi}</Alert>}
        {!form && !okumaHatasi && <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>}
        {form && (
          <>
            {bitis && (
              <Alert fade={false} color={bitis.gitti || !postaGonder ? "success" : "warning"} className="py-2 small" data-testid="kt-onay-sonucu">
                <div>{bitis.cevap?.mesaj}</div>
                <div>{bitis.posta}</div>
                {(bitis.cevap?.pasifYapilanlar || []).length > 0 && (
                  <div>{t("kullaniciTalepleri.pasifYapildi", { count: bitis.cevap.pasifYapilanlar.length })}</div>
                )}
              </Alert>
            )}
            {hata && <Alert fade={false} color="danger" className="py-2 small">{hata}</Alert>}
            <div className="small text-muted mb-3">
              <span className="fw-semibold text-body">{talep.firmaAdi || `#${talep.firmaId}`}</span>
              {talep.fuarAdi && <> · {talep.fuarAdi}</>}
              {" · "}{t("kullaniciTalepleri.talepEden")}: {talep.talepEdenAdi || talep.talepEdenKullaniciAdi || "—"}
              {" · "}{tarihSaat(talep.olusturmaTarihi)}
            </div>
            {detay.kullaniciAdiAlinmis && (
              <Alert fade={false} color="warning" className="py-2 small" data-testid="kt-adi-alinmis">{t("kullaniciTalepleri.kullaniciAdiAlinmis", { kullaniciAdi: talep.kullaniciAdi })}</Alert>
            )}
            {!detay.kullaniciAdiAlinmis && detay.kullaniciAdiBaskaTalepte && (
              <Alert fade={false} color="info" className="py-2 small">{t("kullaniciTalepleri.kullaniciAdiBaskaTalepte", { kullaniciAdi: talep.kullaniciAdi })}</Alert>
            )}
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="kt-kullanici-adi">{t("kullaniciTalepleri.kullaniciAdi")} *</Label>
                  <Input id="kt-kullanici-adi" value={form.kullaniciAdi} autoComplete="off" disabled={kilitli}
                    onChange={(e) => ayarla("kullaniciAdi", e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="kt-firma">{t("kullaniciTalepleri.firma")}</Label>
                  <Input id="kt-firma" value={talep.firmaAdi || `#${talep.firmaId}`} readOnly disabled />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="kt-ad">{t("kullaniciTalepleri.ad")} *</Label>
                  <Input id="kt-ad" value={form.ad} disabled={kilitli} onChange={(e) => ayarla("ad", e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="kt-soyad">{t("kullaniciTalepleri.soyad")} *</Label>
                  <Input id="kt-soyad" value={form.soyad} disabled={kilitli} onChange={(e) => ayarla("soyad", e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="kt-eposta">{t("kullaniciTalepleri.eposta")} *</Label>
                  <Input id="kt-eposta" value={form.eposta} inputMode="email" disabled={kilitli} onChange={(e) => ayarla("eposta", e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="kt-telefon">{t("kullaniciTalepleri.telefon")}</Label>
                  <TelefonGirdisi id="kt-telefon" value={form.telefon} disabled={kilitli} onChange={(v) => ayarla("telefon", v)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="kt-unvan">{t("kullaniciTalepleri.unvan")}</Label>
                  <Input id="kt-unvan" value={form.unvan} disabled={kilitli} onChange={(e) => ayarla("unvan", e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="kt-rol">{t("kullaniciTalepleri.portalRolu")}</Label>
                  <Input id="kt-rol" type="select" value={form.portalRolId} disabled={kilitli} onChange={(e) => ayarla("portalRolId", e.target.value)}>
                    {(detay.roller || []).map((r) => <option key={r.portalRolId} value={r.portalRolId}>{r.ad}</option>)}
                  </Input>
                  {String(talep.portalRolId) !== form.portalRolId && <div className="form-text">{t("kullaniciTalepleri.rolDegisti", { rol: talep.portalRolAdi || "" })}</div>}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label for="kt-grup">{t("kullaniciTalepleri.kullaniciGrubu")}</Label>
                  <Input id="kt-grup" type="select" value={form.userGroupId} disabled={kilitli} onChange={(e) => ayarla("userGroupId", e.target.value)}>
                    {(detay.userGroupIds || []).map((id) => <option key={id} value={id}>{grupAdi(id)}</option>)}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {kullananlar.length > 0 && (
              <div className="border rounded p-2 mb-3" data-testid="kt-eposta-kullananlar">
                <div className="fw-semibold small mb-1">{t("kullaniciTalepleri.epostaBaskaGirislerde", { eposta: talep.eposta })}</div>
                <div className="small text-muted mb-2">{epostaDegisti ? t("kullaniciTalepleri.epostaDegistiPasifYok") : t("kullaniciTalepleri.pasifYapilacakIpucu")}</div>
                {kullananlar.map((k) => {
                  const secilebilir = k.pasifYapilabilir && !epostaDegisti;
                  return (
                    <FormGroup check key={k.userId} className="mb-1">
                      <Input
                        type="checkbox" id={`kt-pasif-${k.userId}`} data-testid={`kt-pasif-${k.userId}`}
                        checked={secilebilir && pasifSecim.has(k.userId)} disabled={kilitli || !secilebilir}
                        onChange={(e) => setPasifSecim((s) => { const y = new Set(s); if (e.target.checked) y.add(k.userId); else y.delete(k.userId); return y; })}
                      />
                      <Label check for={`kt-pasif-${k.userId}`} className="small">
                        <span className="fw-semibold">{k.userLoginName}</span>
                        {" "}{[k.userName, k.userSurname].filter(Boolean).join(" ")}
                        {(k.firmalar || []).length > 0 && <span className="text-muted"> · {k.firmalar.map((f) => f.firmaAdi).join(", ")}</span>}
                        {k.userLastLogin && <span className="text-muted"> · {t("kullaniciTalepleri.sonGiris")}: {tarihSaat(k.userLastLogin)}</span>}
                        {k.personel && <Badge color="dark" className="ms-1 fw-normal">{t("kullaniciTalepleri.personel")}</Badge>}
                        {!k.personel && !k.userActive && <Badge color="secondary" className="ms-1 fw-normal">{t("kullaniciTalepleri.zatenPasif")}</Badge>}
                      </Label>
                    </FormGroup>
                  );
                })}
              </div>
            )}

            <FormGroup check className="mb-0">
              <Input id="kt-posta" type="checkbox" checked={postaGonder} disabled={kilitli} onChange={(e) => setPostaGonder(e.target.checked)} />
              <Label for="kt-posta" check>{t("userFormModal.aktivasyonMailiGonder")}</Label>
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={kapat} disabled={saving}>{bitis ? t("common:kapat") : t("common:iptal")}</Button>
        {form && !bitis && (
          <Button color="success" onClick={onayla} disabled={saving} data-testid="kt-onayla">
            {saving ? <Spinner size="sm" /> : t("kullaniciTalepleri.onaylaOlustur")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

// ─── Reject ──────────────────────────────────────────────────────────────────

export function KullaniciTalebiRedModal({ talep, onClose }) {
  const { t } = useTranslation("yonetim");
  const [sebep, setSebep] = useState("");
  const [saving, setSaving] = useState(false);
  const [hata, setHata] = useState(null);

  const kapat = () => { if (!saving) onClose(null); };

  const reddet = async () => {
    setHata(null);
    if (!sebep.trim()) { setHata(t("kullaniciTalepleri.redSebebiZorunlu")); return; }
    setSaving(true);
    try {
      onClose(await reddetKullaniciTalebi(talep.id, sebep));
    } catch (e) {
      setHata(talepHatasi(e) || t("kullaniciTalepleri.reddedilemedi"));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen toggle={kapat}>
      <ModalHeader toggle={kapat}>{t("kullaniciTalepleri.redBaslik")}</ModalHeader>
      <ModalBody>
        {hata && <Alert fade={false} color="danger" className="py-2 small">{hata}</Alert>}
        <p className="small text-muted">
          {t("kullaniciTalepleri.redAciklama", { kisi: `${talep.ad || ""} ${talep.soyad || ""}`.trim(), firma: talep.firmaAdi || `#${talep.firmaId}` })}
        </p>
        <FormGroup className="mb-0">
          <Label for="kt-red-sebebi">{t("kullaniciTalepleri.redSebebi")} *</Label>
          <Input
            id="kt-red-sebebi" type="textarea" rows={4} value={sebep} maxLength={RED_SEBEBI_EN_FAZLA} disabled={saving}
            onChange={(e) => setSebep(e.target.value)}
          />
          <div className="form-text text-end">{sebep.length} / {RED_SEBEBI_EN_FAZLA}</div>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={kapat} disabled={saving}>{t("common:iptal")}</Button>
        <Button color="danger" onClick={reddet} disabled={saving} data-testid="kt-reddet">
          {saving ? <Spinner size="sm" /> : t("kullaniciTalepleri.reddet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
